import { IPartner } from "api/types/_news";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownOption from "components/Common/DropdownOption";
import InputsRange from "components/Common/InputsRange";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { filterMediaOptions, formatNumberWithCommas } from "helpers/format";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { downloadExcelMediaStatistics, getAllPartners, mediaStatistics as onGetMediaStatistics } from "../../../store/thunks";
import NestingFilter from "pages/News/Components/NestingFilter";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";


const SORT_BY_DEFAULT = 'impression_sum';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const MediaStatistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        media_id: withDefault(StringParam, ''),
        start_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        publish_at_start: withDefault(StringParam, `${moment().subtract(30, 'days').format("Y-MM-DD")}`),
        publish_at_end: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        partner_id: withDefault(StringParam, ''),
        impression_sum_start: withDefault(StringParam, ''),
        impression_sum_end: withDefault(StringParam, ''),
        click_sum_start: withDefault(StringParam, ''),
        click_sum_end: withDefault(StringParam, ''),
        ctr_sum_start: withDefault(StringParam, ''),
        ctr_sum_end: withDefault(StringParam, ''),
        // approved_article_count_start: withDefault(StringParam, ''),
        // approved_article_count_end: withDefault(StringParam, ''),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const StatisticProperties = createSelector(
        selectLayoutState,
        (state) => ({
            media: state.media,
            isMediaSuccess: state.isMediaSuccess,
            isMediaLoading: state.isMediaLoading,
            error: state.error,
        })
    );

    const [isLoadingExportFile, setIsLoadingExportFile] = useState<boolean>(false);

    const { media, isMediaLoading } = useSelector(StatisticProperties);
    const [isCallAllOptionLoading, setIsCallAllOptionLoading] = useState<boolean>(true);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [datePublishedRangeSearch, setDatePublishedRangeSearch] = useState<any[]>([query?.publish_at_start ? moment(query?.publish_at_start, 'Y-MM-DD').toDate() : null, query?.publish_at_end ? moment(query?.publish_at_end || '', 'Y-MM-DD').toDate() : null]);
    const [startDatePublished, endDatePublished] = datePublishedRangeSearch;

    const [startImpressionSearch, setStartImpressionSearch] = useState<string>(query?.impression_sum_start || "");
    const [endImpressionSearch, setEndImpressionSearch] = useState<string>(query?.impression_sum_end || "");

    const [startClickSearch, setStartClickSearch] = useState<string>(query?.click_sum_start || "");
    const [endClickSearch, setEndClickSearch] = useState<string>(query?.click_sum_end || "");

    const [startCtrSearch, setStartCtrSearch] = useState<string>(query?.ctr_sum_start || "");
    const [endCtrSearch, setEndCtrSearch] = useState<string>(query?.ctr_sum_end || "");

    const [startApprovedArticleCountSearch, setStartApprovedArticleCountSearch] = useState<string>(query?.approved_article_count_start || "");
    const [endApprovedArticleCountSearch, setEndApprovedArticleCountSearch] = useState<string>(query?.approved_article_count_end || "");


    const [mediaSearch, setMediaSearch] = useState<Option | null>(!!query?.partner_id ? { value: query?.partner_id, label: '' } : null);

    const [partners, setPartners] = useState<IPartner[]>([]);

    useEffect(() => {
        dispatch(onGetMediaStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            publish_at_start: startDatePublished ? moment(new Date(startDatePublished)).format("Y-MM-DD") : '',
            publish_at_end: endDatePublished ? moment(new Date(endDatePublished)).format("Y-MM-DD") : '',
            partner_id: mediaSearch?.value || '',
            impression_sum_start: startImpressionSearch || '',
            impression_sum_end: endImpressionSearch || '',

            click_sum_start: startClickSearch || '',
            click_sum_end: endClickSearch || '',

            ctr_sum_start: startCtrSearch || '',
            ctr_sum_end: endCtrSearch || '',

            approved_article_count_start: startApprovedArticleCountSearch || '',
            approved_article_count_end: endApprovedArticleCountSearch || '',

            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: `${moment().format("Y-MM-DD")}`,
            end_date: `${moment().format("Y-MM-DD")}`,
            publish_at_start: `${moment().subtract(30, 'days').format("Y-MM-DD")}`,
            publish_at_end: `${moment().format("Y-MM-DD")}`,
            partner_id: '',
            impression_sum_start: '',
            impression_sum_end: '',
            click_sum_start: '',
            click_sum_end: '',
            ctr_sum_start: '',
            ctr_sum_end: '',
            approved_article_count_start: '',
            approved_article_count_end: '',
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
        setDatePublishedRangeSearch([moment(queryNew?.publish_at_start || "", 'Y-MM-DD').toDate(), moment(queryNew?.publish_at_end || "", 'Y-MM-DD').toDate()]);
        setMediaSearch({ label: t('All'), value: '' });
        setStartImpressionSearch(queryNew?.impression_sum_start);
        setEndImpressionSearch(queryNew?.impression_sum_end);
        setStartClickSearch(queryNew?.click_sum_start);
        setEndClickSearch(queryNew?.click_sum_end);
        setStartCtrSearch(queryNew?.ctr_sum_start);
        setEndCtrSearch(queryNew?.ctr_sum_end);
        setStartApprovedArticleCountSearch(queryNew?.approved_article_count_start);
        setEndApprovedArticleCountSearch(queryNew?.approved_article_count_end);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Ranking'),
                accessor: "index",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-start',
                thWidth: 90,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            {!!item?.text_row ? <></> : <div className="text-start" style={{ minWidth: '60px' }}>
                                {cell?.value}
                            </div>}
                        </>
                    )
                },
            },
            {
                Header: t('Media'),
                accessor: "partner_id",
                filterable: true,
                sortable: false,
                thWidth: 170,
                thClass: 'align-middle',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const _media = partners?.find((_m: any) => String(_m?.id) === String(cell?.value));
                    return (<>
                        {item?.text_row ? <strong>{item?.text_row}</strong> :
                            (<div className="d-flex align-items-center" style={{ minWidth: '150px' }}>
                                {_media?.name || '-'}
                            </div>)}
                    </>)
                },
            },
            {
                Header: t('Articles'),
                accessor: "article_count",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 120 : 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '100px' : '80px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Approved Articles'),
                accessor: "approved_article_count",
                filterable: true,
                sortable: true,
                thWidth: i18n?.language === 'en' ? 180 : 130,
                thClass: 'align-middle text-end',
                description: t("Updated hourly"),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-info" style={{ minWidth: i18n?.language === 'en' ? '120px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Approval Rate'),
                accessor: "approval_rate",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 160 : 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '140px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)} %</div>
                    </>)
                },
            },
            {
                Header: t('Naver Articles'),
                accessor: "naver_article_count",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 160 : 150,
                description: t('This is the number of articles that have appeared at least once on the first page of Naver News search results.'),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-info" style={{ minWidth: i18n?.language === 'en' ? '140px' : '130px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Exposure Rate'),
                accessor: "exposure_rate",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 150 : 110,
                description: t("This is the exposure rate of approved articles on Naver News."),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-info" style={{ minWidth: i18n?.language === 'en' ? '120px' : '90px' }}>{formatNumberWithCommas(cell?.value || 0)} %</div>
                    </>)
                },
            },
            {
                Header: t('10 Click Articles'),
                accessor: "current_click_10_count",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 170 : 150,
                description: t('The number of articles with 10 or more clicks on the current day.'),
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-info" style={{ minWidth: i18n?.language === 'en' ? '150px' : '130px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Impressions per Article'),
                accessor: "impression_per_article",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 200 : 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '140px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Clicks per Article'),
                accessor: "click_per_article",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 160 : 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (<>
                        <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '140px' : '100px' }}>{formatNumberWithCommas(cell?.value || 0)}</div>
                    </>)
                },
            },
            {
                Header: t('Impression'),
                accessor: "impression_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 120,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Click'),
                accessor: "click_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 120,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('CTR'),
                accessor: "ctr_sum",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-end',
                thWidth: 120,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: t('Total Media Impressions'),
                accessor: "total_media_impression_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 230 : 160,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: i18n?.language === 'en' ? '200px' : '130px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Total Media Clicks'),
                accessor: "total_media_click_sum",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 170 : 140,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: i18n?.language === 'en' ? '140px' : '110px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Total Media CTR'),
                accessor: "total_media_ctr_sum",
                filterable: true,
                sortable: false,
                thClass: 'align-middle text-end',
                thWidth: i18n?.language === 'en' ? 150 : 130,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end" style={{ minWidth: i18n?.language === 'en' ? '120px' : '100px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: '',
                accessor: "___",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '80px' }}></div>
                        </>
                    )
                },
            },

        ],
        [i18n?.language, userPermissions, JSON.stringify(partners)]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    const handleChangeDatePublishedRangePicker = (values: any[] = []) => {
        setDatePublishedRangeSearch((_prev: any) => values);
    }


    const handleDownloadExcel = async () => {
        try {
            setIsLoadingExportFile((_prev) => true);
            const response: any = await downloadExcelMediaStatistics({ ...query });
            if (response?.data) {
                setIsLoadingExportFile((_prev) => false);
                const link = document.createElement('a');
                link.href = response?.data?.url;
                link.download = 'boosting_keywords';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setIsLoadingExportFile((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoadingExportFile((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                setIsCallAllOptionLoading((_prev) => true);
                const [res1]: any = await Promise.all([getAllPartners()]);
                setIsCallAllOptionLoading((_prev) => false);
                setPartners((_prev: any) => res1?.data);
                if (query?.partner_id) {
                    const _partner = res1?.data?.find((_p: any) => String(_p?.id) === String(query?.partner_id));
                    setMediaSearch({ value: `${_partner?.id}`, label: _partner?.name });
                }

            } catch (error: any) {
                setIsCallAllOptionLoading((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {
        document.title = `${t('Media Statistics')} | ${t('PRESS Ai')}`;
        document.body.classList.remove('vertical-sidebar-enable');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Media Statistics')} pageTitle={t('Statistics')} />
                    {isHavePermissionRole(ROLES_FOR_APP.MEDIA_STATISTICS, userPermissions) && (
                        <Row>
                            <Col sm={12} md={12}>
                                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                    <div className="ribbon round-shape top-info-lg" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem' }}>{t('This data is analyzed per 10 mins')}</div>
                                    <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                        <Row className="g-4 align-items-center">
                                            <div className="col-lg-3 mt-5 mt-sm-4">
                                                <div>
                                                    <h5 className="card-title mb-0">
                                                        <span className="me-2">{t('Total')}:</span>
                                                        {(isMediaLoading) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                                            start={0}
                                                            end={media?.pagination?.total || 0}
                                                            duration={1}
                                                            className="text-primary"
                                                        />)}
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="col-lg-9 text-end mt-3 mt-lg-4">
                                                <div className={`d-flex gap-3 flex-wrap justify-content-end padding-lg-right-info-${i18n?.language}`}>
                                                    {isHavePermissionRole(ROLES_FOR_APP.KEYWORD_EXCLUDE_LIST, userPermissions) && (<button
                                                        type="button"
                                                        className="btn btn-success add-btn rounded-pill mb-2 mb-sm-0"
                                                        onClick={handleDownloadExcel}
                                                        disabled={isLoadingExportFile}
                                                    >
                                                        {isLoadingExportFile ? <Spinner size="sm" ></Spinner> : <i className="ri-download-line align-bottom"></i>}
                                                        <span className="ms-1">{t('Button Download Excel')}</span>
                                                    </button>)}
                                                </div>
                                            </div>
                                        </Row>
                                    </CardHeader>
                                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4 px-0 px-lg-3 pt-0 pt-lg-3">
                                        <NestingFilter>
                                            <Row className="g-4 align-items-center mt-0">
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 mt-md-2' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startImpressionSearch}
                                                        valueTo={endImpressionSearch}
                                                        placeholderFrom={`${t('Impression (Start)')}...`}
                                                        placeholderTo={`${t('Impression (End)')}...`}
                                                        labelFrom={`${t('Impression (Start)')}`}
                                                        labelTo={`${t('Impression (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartImpressionSearch(val)}
                                                        onChangeTo={(val) => setEndImpressionSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 mt-md-2' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startClickSearch}
                                                        valueTo={endClickSearch}
                                                        placeholderFrom={`${t('Click (Start)')}...`}
                                                        placeholderTo={`${t('Click (End)')}...`}
                                                        labelFrom={`${t('Click (Start)')}`}
                                                        labelTo={`${t('Click (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartClickSearch(val)}
                                                        onChangeTo={(val) => setEndClickSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                                <Col sm={6} md={6} lg={6} xl={6} xxl={4} className='mt-3 mt-xxl-2' >
                                                    <InputsRange
                                                        nameFrom="from"
                                                        nameTo="to"
                                                        valueFrom={startCtrSearch}
                                                        valueTo={endCtrSearch}
                                                        placeholderFrom={`${t('CTR (Start)')}...`}
                                                        placeholderTo={`${t('CTR (End)')}...`}
                                                        labelFrom={`${t('CTR (Start)')}`}
                                                        labelTo={`${t('CTR (End)')}`}
                                                        isTypeNumber={true}
                                                        onChangeFrom={(val) => setStartCtrSearch(val)}
                                                        onChangeTo={(val) => setEndCtrSearch(val)}
                                                        onKeyDownFrom={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                        onKeyDownTo={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                                <Col sm={6} md={6} xl={6} xxl={4} className='mt-3'>
                                                    <LabelWrapper label={t('Media')} isShow={true}>
                                                        <DropdownOption
                                                            name="media"
                                                            dataList={filterMediaOptions(partners || [])?.map((_p: any) => ({ label: _p?.name, value: `${_p?.id}` }))}
                                                            placeholder={`${t("Media")}...`}
                                                            className="dropdown-status-rounded z-hight"
                                                            classNamePrefix="name-prefix"
                                                            initialValue={mediaSearch || null}
                                                            onChangeSelect={(e: any) => setMediaSearch(e)}
                                                            isHasOptionAll={true}
                                                            optionAll={{ label: t('All'), value: '' }}
                                                        />
                                                    </LabelWrapper>
                                                </Col>

                                                <Col sm={6} md={6} xl={6} xxl={4} className='date-picker-wrapper-custom mt-3'>
                                                    <LabelWrapper label={t('Published At')} isShow={!!startDatePublished || !!endDatePublished}>
                                                        <DatePickerCustom
                                                            placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                            startDate={startDatePublished || null}
                                                            endDate={endDatePublished || null}
                                                            onChangePicker={handleChangeDatePublishedRangePicker}
                                                            showOptions={[
                                                                'today', 'yesterday', 'two_day_ago',
                                                                'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                                'this_week', 'last_week',
                                                                'this_month', 'last_month',
                                                            ]}
                                                        />
                                                    </LabelWrapper>
                                                </Col>

                                                <Col sm={6} md={6} xl={6} xxl={4} className='date-picker-wrapper-custom mt-3'>
                                                    <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                        <DatePickerCustom
                                                            placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                            startDate={startDate || null}
                                                            endDate={endDate || null}
                                                            onChangePicker={handleChangePicker}
                                                            showOptions={[
                                                                'today', 'yesterday', 'two_day_ago',
                                                                'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                                'this_week', 'last_week',
                                                                'this_month', 'last_month',
                                                            ]}
                                                        />
                                                    </LabelWrapper>
                                                </Col>



                                                <Col sm={12} md={12} xl={12} xxl={12} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-xl-end mt-3">
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary me-2 "
                                                            onClick={searchData}
                                                            disabled={(isMediaLoading || isCallAllOptionLoading)}
                                                        >
                                                            {(isMediaLoading || isCallAllOptionLoading) ? <div className="me-1" style={{ display: 'inline-table', verticalAlign: 'top' }}><Spinner size="sm" style={{ width: '14px', height: '14px' }}></Spinner></div> : <i className="ri-search-line align-bottom me-1"></i>}{" "}
                                                            {t('Button Search')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary fs-14"
                                                            onClick={resetData}
                                                        >
                                                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                            {t('Button Reset')}
                                                        </button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </NestingFilter>
                                    </div>
                                    <CardBody className="pt-3">
                                        <TableContainer
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed table-have-first-row-is-total sticky-table-report-media"
                                            theadClass="table-light text-muted"
                                            columns={columns}
                                            data={media?.list?.length ? (
                                                [
                                                    {
                                                        ...media?.total,
                                                        text_row: t('Sum')
                                                    },
                                                    ...media?.list]
                                            ) : []}
                                            customPageSize={query.limit}
                                            customPageIndex={query.page - 1}
                                            totalRecords={media?.pagination?.total}
                                            customPageCount={Math.ceil(Number(media?.pagination?.total) / Number(media?.pagination?.limit))}
                                            handleChangePage={handleChangePage}
                                            manualSorting={true}
                                            sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                            handleChangeSorting={handleChangeSorting}
                                            isLoading={isMediaLoading}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default MediaStatistic;
